/**
 * `R2-UI7` · **Pestaña 5 · Auditoría de cambios.** `PRD_R2:2228-2255`, Regla 26.
 * `// ADR-0354` (el cable) · `// ADR-0356` (el anclaje del vocabulario).
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ RASTRO `[22/9/2026]` · **ESTE ARCHIVO ERA LA MAQUETA VACÍA DE `R1.1`, Y
 * «VACÍA ERA EL REQUISITO» YA NO RIGE** · `ADR-0082`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * El encabezado que estaba acá decía, textual:
 *
 *   ~~*«`CA-85`, `CA-86` y `CA-87` (PRD:593-595) piden tres cosas y ninguna más:
 *   el título «Auditoría de cambios», las columnas Fecha · Usuario · Sección ·
 *   Cambio, y una tabla **VACÍA** que diga «Todavía no hay cambios para
 *   mostrar».»*~~
 *
 *   ~~*«⛔⛔ **VACÍA ES EL REQUISITO, NO UN ESTADO PROVISORIO.** … copiarlas acá
 *   sería **inventar una bitácora**»*~~
 *
 *   ~~*«⛔ **Esta pestaña no lee del borde, y no es que falte una llamada:** el
 *   borde de configuración … tiene seis rutas y ninguna devuelve la bitácora»*~~
 *
 * **`R1.1-F3-CA-85`, `CA-86` y `CA-87` quedan DEROGADOS por `CA-F6-63`**
 * (`PRD_R2:2417`): *«La pestaña de auditoría deja de estar vacía … hay al menos
 * una fila.»* `r2-ui7.md:33` lo dice con esas palabras.
 *
 * ⭐ **Y lo que el párrafo tachado protegía sigue en pie, palabra por palabra:**
 * las filas **no se inventan**. Las que se dibujan son las que devolvió
 * `GET auditoria`; sin filas, la tabla lo dice —que ahora es `CA-F6-69`, no un
 * estado provisorio—. Lo que venció es *«el borde no tiene esa ruta»*: hoy la
 * tiene (`borde-configuracion.ts` ⟦la séptima⟧, `R2-CPL1`/`R2-AW5`).
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⚠️ LO QUE ESTE ARCHIVO **SIGUE** SIN HACER COMO LO PIDE `R1.1-F3-CA-88`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `CA-88` (PRD:596): *«el control «Fecha ↓» tiene el atributo `disabled`»*. **Acá
 * «Fecha ↓» sigue siendo el encabezado de una columna y no hay ningún control**,
 * y `r2-ui7.md:30` lo confirma para R2: *«encabezado con `aria-sort`, sin
 * botón»*.
 *
 * ⛔ **No es una omisión heredada por inercia: es la misma incompatibilidad, y se
 * volvió a medir.** `pruebas/aw48-configuracion-del-chatbot.test.tsx` fija por
 * igualdad exacta la lista de TODOS los controles apagados de la pantalla entera,
 * recorriendo `button, input, textarea, select` del contenedor completo y **sin
 * navegar** (las cinco pestañas están montadas, `pestanas.tsx`). Un botón
 * «Fecha ↓» con `disabled` entraría en esa lista **y no tendría prueba de vida**
 * —nunca se enciende—, que es justo lo que `R1-F2-22` prohíbe: el control apagado
 * PARA SIEMPRE.
 *
 * ⭐ Y `PRD_R2:2284` va en la misma dirección: la pestaña no tiene **filtros,
 * búsqueda, orden ni exportación**. Un encabezado con `aria-sort` y sin nada con
 * qué interactuar es inerte *por construcción*.
 *
 * ⇒ Sigue siendo **un hallazgo para quien mantiene el criterio**, no un pendiente
 * de esta etapa.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐⭐⭐ LAS DOS TABLAS SE ESCRIBEN A MANO, Y UN EXAMEN LAS ANCLA · `ADR-0356`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `AUDIT_SECTIONS` (10 prefijos desde `R4-CPL0`) y `AUDIT_ACTION_LABELS` (30 acciones desde `R4-CPL0`) viven en
 * `contracts/platform/http.ts:419` y `:519` y son **objetos de VALOR**.
 * ⚠️ RASTRO `[28/9/2026]` · acá decía `:444` y después `:466`, y las dos veces quedó
 * viejo: `R3-CPL1` sumó las dos acciones de F5 y `R4-CPL0` (A7) sumó ocho rótulos
 * más, así que las claves corrieron hacia abajo de nuevo. Hoy `AUDIT_ACTION_LABELS`
 * está en `:519` y `AUDIT_SECTIONS` en `:419` (y `AUDIT_ACTION_HOLE_KEYS`, en
 * `:654`). Un número de línea en un comentario envejece solo: por eso el ancla
 * real es el EXAMEN, no esta cita.
 * `vite.config.ts:10-16` prohíbe importar valores de `contracts/` **y lo hace
 * cumplir la herramienta**: el `tsconfig` sí tiene el `paths`, así que `tsc` pasa
 * y **el build rompe** (`ADR-0330`).
 *
 * El molde de `R2-UI5` —un alias de TIPO por entrada más el literal a mano— **no
 * escala a 40 entradas**: serían 40 alias que nadie mantiene.
 *
 * ⇒ **Los literales se escriben a mano acá, y UN SOLO examen los ancla**:
 * `pruebas/r2ui7-anclajes-de-auditoria.test.ts` importa las dos tablas como
 * VALORES —legal de ese lado y sólo de ese lado: `pruebas/` no la empaqueta
 * `vite`, corre bajo `bun`, que resuelve los `paths` del `tsconfig`— y afirma
 * **igualdad profunda** contra estas copias. Precedente vivo:
 * `pruebas/r2ui6-anclajes-del-contrato.test.ts` (`ADR-0353`) y
 * `pruebas/aw48-configuracion-del-chatbot.test.tsx:47`.
 *
 * ⛔ **La igualdad es profunda y en las DOS direcciones**: una clave de más, una
 * de menos o un rótulo cambiado ponen el examen en rojo. Medido por mutación el
 * 22/9/2026 (ver `R2-UI7.md`).
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ LO QUE ESTA PESTAÑA NO HACE, Y CADA «NO» TIENE SU FUENTE
 * ════════════════════════════════════════════════════════════════════════════
 *
 *   · **No tiene `switch (action)` ni tabla propia de frases.** `RT-11` +
 *     `r2-ui7.md` guard 1: una tabla propia «para no depender de A» es la segunda
 *     copia del vocabulario, y en cuanto F2/F4 agreguen acciones la pestaña
 *     muestra crudo lo que el contrato ya traduce.
 *   · **No tiene `filter(...)`, y esto es lo que de verdad importa.** `RT-11`,
 *     textual: *«Una `action` que la pestaña no conozca se muestra con su valor
 *     crudo en vez de esconder la fila.»* Esconderla convertiría una acción sin
 *     etiquetar en una acción **sin rastro**.
 *   · **No filtra ni agrupa por brazo, y no tiene columna de brazo.**
 *     `PRD_R2:2234-2239` fija CUATRO columnas y Regla 26 pide los dos brazos
 *     juntos. ⚠️ `A10-7`: la configuración escribe **una fila por brazo**
 *     (`ADR-0258`), así que un cambio de textos deja DOS filas seguidas y **la
 *     pestaña no las colapsa** (`CA-F6-66` lo dice).
 *   · **No reordena.** El orden lo fija `readPage` y lo une `admin-web`
 *     (⟦el-orden-lo-fija-readpage⟧). Reordenar acá sería reordenar una ventana.
 *   · **No usa un paginado propio.** `paginado.tsx`, el compartido, **sin
 *     modificarlo** (`r2-ui7.md` guard 2 y `CA-F6-77`: «el mismo comportamiento
 *     que las otras tablas»).
 *   · **No comprueba el rol.** La transversal entera es `403` para el
 *     Acompañante (`CA-F6-38`, `R2-UI6`/`R2-AW5`); una segunda comprobación acá
 *     sería una segunda opinión sobre quién entra.
 */

import { useCallback, useEffect, useState, type JSX } from "react"

import { useBordeConfiguracion } from "../contexto-borde-configuracion.tsx"
import { formatearFecha } from "../formato-fecha.ts"
import { Paginado } from "../paginado.tsx"
import type { FilaDeAuditoria } from "./borde-configuracion.ts"

/**
 * ⭐⭐ **COPIA A MANO de `AUDIT_SECTIONS` (`contracts/platform/http.ts:419`).**
 * No se importa: es un VALOR. Ver el bloque `ADR-0356` del encabezado.
 *
 * ⛔ **Las claves llevan el punto final y NO es cosmético**: son PREFIJOS de
 * `entity` (`chatbot.textos` ⇒ `chatbot.` ⇒ «Configuración del chatbot»). Sin el
 * punto, `chatbot` también sería prefijo de `chatbotalgo`.
 *
 * ⚠️ Hay dos claves que dan la MISMA sección —`metricas.` y `analytics.`— y eso
 * es del contrato, no un error de copia: *«las acciones que existen hoy usan
 * `analytics.`; `metricas.` está porque el vocabulario lo nombra de las dos
 * maneras»*. Se copia tal cual; el examen de anclaje no perdona una de menos.
 *
 * `Readonly<Record<string, string>>` y no una unión cerrada, por lo mismo que el
 * contrato: la lista crece con cada feature.
 */
export const SECCIONES_DE_AUDITORIA: Readonly<Record<string, string>> = {
  "chatbot.": "Configuración del chatbot",
  "knowledge.": "Banco de contenido",
  "dispatch.": "Envíos",
  "exports.": "Exportación",
  "conversaciones.": "Conversaciones",
  "participantes.": "Participantes",
  "metricas.": "Métricas",
  "analytics.": "Métricas",
  "plantillas.": "Plantillas",
  // ⭐ ENMIENDA [R4-UIA0] · DE NUEVE A DIEZ, CON RASTRO (`ADR-0082`): `R4-CPL0`
  // sumó `situaciones-criticas.` ⇒ «Situaciones críticas» a `AUDIT_SECTIONS`
  // (`contracts/platform/http.ts`, docblock «ENMIENDA [R4-CPL0]»). La copia
  // tiene que decirlo igual, clave por clave y texto por texto (`ADR-0356`):
  // el ancla `pruebas/r2ui7-anclajes-de-auditoria.test.ts` compara las dos.
  "situaciones-criticas.": "Situaciones críticas",
}

/**
 * ⭐⭐ **COPIA A MANO de `AUDIT_ACTION_LABELS` (`contracts/platform/http.ts:519`).**
 * Ver el bloque `ADR-0356` del encabezado.
 *
 * ⭐ **RASTRO `[28/9/2026]` · `R3-UI7`.** `R3-CPL1` cambió dos frases de
 * Participantes —el opt-out pasó a «Marcó el opt-out…» y la baja revertida ganó
 * su punto final— y agregó `participantes.alta.creada` y
 * `participantes.ingesta.aceptada`: eran veinte y pasaron a veintidós.
 * `R4-CPL0` (A7) sumó después las ocho de R4 y hoy son **treinta** (ver el
 * encabezado y la enmienda `[R4-UIA0]` en la tabla, abajo). El contrato deja
 * escrito que *«ninguna forma de contar da dieciséis»* —el número que dice
 * `PRD_R2:2243`— y que quien corrige el PRD es la PO. Esta copia lista lo que el
 * contrato lista, no lo que el PRD dice que hay, y el ancla
 * `pruebas/r2ui7-anclajes-de-auditoria.test.ts` compara las dos tablas entrada
 * por entrada.
 *
 * ⛔ **Una acción que no esté acá se muestra con su valor CRUDO y la fila no se
 * esconde nunca.** `RT-11`, textual. Es la mitad que de verdad importa.
 */
export const ROTULOS_DE_ACCION: Readonly<Record<string, string>> = {
  // ── Las 13 que ya se escriben en el árbol ──
  // R3: era «Cambió el estado de un participante»; desde R3 esa ruta sólo acepta
  // opt-out (B8, `R3-F2` RT 1), así que cambia la frase y gana el punto final.
  "participantes.flag.cambiado": "Marcó el opt-out de un participante.",
  "chatbot.textos.guardados": "Guardó los textos del chatbot",
  "chatbot.guardrail.creado": "Creó una regla de seguridad",
  "chatbot.guardrail.editado": "Editó una regla de seguridad",
  "chatbot.guardrail.borrado": "Borró una regla de seguridad",
  "chatbot.guardrails.reordenados": "Reordenó las reglas de seguridad",
  "dispatch.envio.creado": "Creó un envío",
  "dispatch.envio.cancelado": "Canceló un envío",
  "conversaciones.exportacion.generada": "Descargó el CSV de Conversaciones",
  "exports.exportacion.solicitada": "Solicitó una exportación",
  "exports.exportacion.descargada": "Descargó una exportación",
  "knowledge.documento.cargado": "Cargó un documento",
  "knowledge.documento.borrado": "Borró un documento",
  // ── Las 7 que llegan con R2. El nombre se fija en el CONTRATO, antes de escribirse ──
  "plantillas.agregada": "Agregó una plantilla a la lista",
  "plantillas.quitada": "Quitó una plantilla de la lista",
  // R3 le agrega el punto final (`PRD_R3:2173`).
  "participantes.baja.revertida": "Revirtió la baja de un participante.",
  "analytics.metricas.descargadas": "Descargó el CSV de Métricas",
  "plantillas.respuesta.creada": "Creó una respuesta rápida",
  "plantillas.respuesta.editada": "Editó una respuesta rápida",
  "plantillas.respuesta.eliminada": "Eliminó una respuesta rápida",
  // ── Las 2 que llegan con R3. Traen un HUECO con nombre: el lote y el archivo. ──
  "participantes.alta.creada": "Dio de alta a un participante en el lote «[lote]».",
  "participantes.ingesta.aceptada": "Cargó el archivo «[archivo]» en el lote «[lote]».",
  // ── ENMIENDA [R4-UIA0] · DE VEINTIDÓS A TREINTA, CON RASTRO (`ADR-0082`).
  //    Las 8 que `R4-CPL0` (A7) fijó en `AUDIT_ACTION_LABELS` ANTES de que
  //    ninguna ruta escriba la fila. Se copian texto por texto, con el PUNTO
  //    EXACTO del PRD: CON punto las seis de la marca, la exportación y los tres
  //    periodos; SIN punto las dos de CSV de Métricas (`PRD_R4:1735`, :1741).
  // F1 · la marca. `entity`: `situaciones-criticas.caso…` ⇒ «Situaciones críticas».
  //      `PRD_R4:918`, :989. Sin nombre ni teléfono del participante.
  "situaciones-criticas.caso.atendido": "Marcó como atendido un caso.",
  // F2 · la descarga de Exportación. El plural son DOS acciones (A7 (b)), no una
  //      con «participante(s)»: la ruta elige cuál escribir según la cantidad.
  //      `entity`: `exports.exportacion…` ⇒ «Exportación».
  "exports.exportacion.csv.uno": "Descargó la exportación de 1 participante.",
  "exports.exportacion.csv.varios": "Descargó la exportación de [N] participantes.",
  // F3 · cargar, editar y borrar periodo. `entity`: `metricas.periodo…` ⇒
  //      «Métricas». Las tres traen el hueco `[Item]` (ver `HUECOS_DE_ACCION`).
  "metricas.periodo.cargado": "Cargó el periodo «[Item]».",
  "metricas.periodo.editado": "Editó el periodo «[Item]».",
  "metricas.periodo.borrado": "Borró el periodo «[Item]».",
  // F3 · las dos descargas de CSV. `entity`: `metricas.csv…` ⇒ «Métricas».
  "metricas.csv.resultados.descargado": "Descargó el CSV de resultados de Métricas",
  "metricas.csv.errores.descargado": "Descargó el CSV de errores de Métricas",
}

/**
 * ⭐⭐ **COPIA A MANO de `AUDIT_ACTION_HOLE_KEYS` (`contracts/platform/http.ts:654`).**
 * `// ADR-0356`.
 *
 * **Las frases de arriba que traen un HUECO son MOLDES, no texto fijo**: la del
 * alta nombra el lote, la de la carga nombra el archivo y el lote, y **ésos son
 * DATOS de la fila, no de la frase** (`B29`). Este mapa declara, por `action`,
 * qué marcador se rellena con qué clave de `newValue`.
 *
 * ⚠️ RASTRO `[R4-UIA0]` · acá decía «Dos de las frases de arriba son MOLDES»,
 * que era cierto con `R3-UI7` (el alta y la carga) y dejó de serlo cuando
 * `R4-CPL0` (A7) sumó cuatro moldes más (ver `HUECOS_DE_ACCION`, abajo): hoy
 * son **SEIS**.
 *
 * ⛔ **Las claves son `lote`, `archivo`, `filas` e `item`, y NINGUNA puede ser
 * `nombre` ni `telefono`:** `RT 11` prohíbe que la fila de auditoría lleve el
 * dato del participante, y que el relleno los leyera sería la fuga escrita en
 * el vocabulario.
 *
 * ⭐ `R3-UI7` · **esta tabla ya tiene su anclaje, como la de frases.** La igualdad
 * con `AUDIT_ACTION_HOLE_KEYS` se afirma entrada por entrada —`toEqual` profundo—
 * en `pruebas/r2ui7-anclajes-de-auditoria.test.ts`: una acción de más, una de
 * menos o un marcador mapeado a otra clave ponen el test en rojo. Es el único
 * instrumento que ve desincronizarse estas dos copias (`ADR-0356`), porque las
 * dos declaran `Readonly<Record<…>>` y `tsc` las da por buenas aunque digan
 * cosas distintas.
 */
export const HUECOS_DE_ACCION: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  "participantes.alta.creada": { "[lote]": "lote" },
  "participantes.ingesta.aceptada": { "[archivo]": "archivo", "[lote]": "lote" },
  // ── ENMIENDA [R4-UIA0] · DE DOS A SEIS, CON RASTRO (`ADR-0082`). Las cuatro
  //    acciones nuevas de `R4-CPL0` (A7) con molde. El tipo es POR HUECO
  //    (⟦el-tipo-es-por-hueco⟧): `[N]` es la CANTIDAD de filas de datos, un
  //    ENTERO; `[Item]` es el nombre del periodo, TEXTO no vacío.
  "exports.exportacion.csv.varios": { "[N]": "filas" },
  "metricas.periodo.cargado": { "[Item]": "item" },
  "metricas.periodo.editado": { "[Item]": "item" },
  "metricas.periodo.borrado": { "[Item]": "item" },
}

/**
 * Los **datos crudos de la fila** (`newValue`): ahí viajan el lote y el archivo
 * de una alta o de una carga (`B29`).
 *
 * ⛔ `FilaDeAuditoria` los recorta (`borde-configuracion.ts`, que esta etapa no
 * toca), así que se leen con una vista ENSANCHADA. Un `newValue` ausente **no es
 * un error de datos**: es una fila sin el dato, y ahí el hueco del molde queda
 * visible y la fila se dibuja igual.
 */
function datosDeLaFila(fila: FilaDeAuditoria): unknown {
  // ⭐ `[30/9/2026 · R3-UI10 · R3-H-002]` Acá había un CAST
  // ~~`(fila as FilaDeAuditoria & { readonly newValue?: unknown }).newValue`~~ a una vista
  // ensanchada, porque el borde recortaba el campo. **El cast era el problema, no la solución:**
  // hacía compilar la lectura de un campo que el borde NUNCA mandaba, así que `tsc` no podía ver
  // que el hueco quedaba visible siempre. Ahora `newValue` está en `FilaDeAuditoria` y se lee
  // derecho: si alguien lo saca del borde, esto deja de compilar.
  return fila.newValue
}

/** `true` si el valor es un objeto plano. Mismo criterio que el borde. */
function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null && !Array.isArray(valor)
}

/**
 * **La Sección de una fila**: el prefijo de `entity`, o `entity` CRUDO.
 *
 * ⛔ Se elige el prefijo MÁS LARGO que coincida y no «el primero que aparezca»:
 * el orden de las claves de un objeto no es una fuente de verdad, y un día que
 * dos prefijos se solapen el resultado no puede depender de en qué orden se
 * escribieron.
 *
 * ⛔ **Sin prefijo conocido devuelve `entity` tal cual** (`RT-11`): la fila se ve
 * igual, con la entidad cruda en la celda.
 */
export function seccionDeLaEntidad(entity: string): string {
  let elegido: string | null = null
  for (const prefijo of Object.keys(SECCIONES_DE_AUDITORIA)) {
    if (entity.startsWith(prefijo) && (elegido === null || prefijo.length > elegido.length)) {
      elegido = prefijo
    }
  }
  // ⛔ Sin prefijo conocido la celda muestra `entity` CRUDO (`RT-11`): la fila se
  // ve igual. La forma `return entity` explícita es la que el instrumento de
  // `R4-UIA0` reconoce como el fallback (y no un valor inventado en blanco).
  if (elegido === null) return entity
  return SECCIONES_DE_AUDITORIA[elegido] as string
}

/**
 * **El Cambio de una fila**: la frase del contrato, o el `action` CRUDO. `RT-11`.
 *
 * ⭐ `R3-UI7` · las frases de alta y de carga son **moldes**: el lote y el archivo
 * son DATOS de la fila y el reemplazo se hace ACÁ, con `String.prototype.replace`
 * sobre el molde (`B29`) — nunca armando la frase con una plantilla `${…}`.
 * ⚠️ RASTRO `[R4-UIA0]` · `R4-CPL0` (A7) sumó cuatro moldes más —`[N]` en la
 * exportación plural y `[Item]` en los tres periodos (ver `HUECOS_DE_ACCION`)—,
 * así que hoy ésas NO son las únicas frases molde: el mecanismo es el mismo para
 * toda entrada del mapa que declare un hueco.
 *
 * ⛔ Si el dato no vino, **el molde queda literal —el hueco `«[lote]»` visible— y
 * la fila se muestra igual**: nunca un valor inventado, nunca la fila escondida.
 */
export function rotuloDeLaAccion(action: string, datos?: unknown): string {
  const molde = ROTULOS_DE_ACCION[action] ?? action
  const huecos = HUECOS_DE_ACCION[action]
  if (huecos === undefined || !esObjeto(datos)) return molde
  let salida = molde
  for (const [marcador, clave] of Object.entries(huecos)) {
    const valor = datos[clave]
    if (typeof valor === "string" && valor !== "") {
      salida = salida.replace(marcador, valor)
    } else if (
      // ⭐ `R4-UIA0` · EL TIPO ES POR HUECO (⟦el-tipo-es-por-hueco⟧): `[N]` es la
      // CANTIDAD de filas de datos, un ENTERO que la ruta guardó en `filas`
      // (`R4-AWE1`); sin esta rama una fila con `filas: 13` dejaría «[N]»
      // visible aunque el dato esté. `[lote]`, `[archivo]` e `[Item]` siguen
      // aceptando SÓLO cadenas no vacías: un `lote: 123` deja el hueco visible
      // (`r3-ui7-rotulos-de-participantes.test.tsx:204-211`), que es lo que R3
      // decidió y esta etapa NO relaja.
      marcador === "[N]" &&
      typeof valor === "number" &&
      Number.isInteger(valor)
    ) {
      // El formato es `String(n)`, sin separador de miles (lo fija `R4-CPL0`).
      salida = salida.replace(marcador, String(valor))
    }
  }
  return salida
}

/**
 * **El Usuario de una fila**: el nombre resuelto, o el correo CRUDO.
 *
 * `RT-8` (`PRD_R2:2338`), textual: *«si es `null`, el `actor` crudo (el correo)
 * **se muestra**, no se esconde»*. Un `null` no es un error de resolución: es un
 * correo que no es de ningún usuario —las filas de máquina de la ingesta
 * (`ADR-0046`) son el caso real—, y una celda en blanco ahí sería una fila sin
 * responsable.
 */
export function usuarioDeLaFila(fila: FilaDeAuditoria): string {
  return fila.actorName ?? fila.actor
}


/**
 * ⚠️ **ELECCIÓN DE ESTA ETAPA, revertible en una línea.** Ninguna fuente fija la
 * escalera de «Filas por página» de esta pestaña: `CA-F6-77` pide el selector y
 * no dice con qué números, y `envios/formulario.tsx:54` usa
 * `[50,100,…,3000]`, que es una escalera de padrón de participantes y no de una
 * bitácora que se lee de a poco.
 *
 * Se elige una escalera corta que arranca en el `pageSize` que el contrato usa de
 * ejemplo (*«con `pageSize` de 50 la diferencia es entre una consulta y
 * cincuenta»*, `admin-web/src/auditoria.ts`).
 */
const TAMANOS_DE_PAGINA = [25, 50, 100, 200] as const

/** El tamaño de arranque. Ver la nota de `TAMANOS_DE_PAGINA`: es elección, no criterio. */
const TAMANO_INICIAL = 50

/**
 * ⭐⭐⭐ `activa` es OPCIONAL y su default es **`true`**, y ése default es la
 * decisión — no una comodidad.
 *
 * ⛔⛔ **RASTRO `[22/9/2026]` · lo corrigió `gates/pantalla-auditoria.spec.ts`.**
 * Acá el prop era **obligatorio** (`{ readonly activa: boolean }`) y el efecto
 * arrancaba con `if (!activa) return`. Montada sin props —que es como la monta
 * cualquiera que no sea `pantalla.tsx`— la pestaña **no pedía NADA y se quedaba
 * diciendo «Cargando la auditoría…» para siempre**. Medido:
 *
 *     [cable-ruta] la pestaña no pidió ninguna URL que termine en `auditoria`
 *                  (pidió: nada)
 *
 * ⇒ **el default «no leas» falla CERRADO hacia un estado roto e invisible**: un
 * spinner que miente, sin error, sin tabla y sin nada que se ponga rojo. El
 * default correcto es el contrario: **la pestaña hace su trabajo salvo que el
 * padre le diga que está tapada.** `activa` deja de ser una precondición para ser
 * lo que siempre tuvo que ser: una pista del padre sobre CUÁNDO refrescar.
 *
 * ⭐ **Y lo que el prop sí compra se conserva entero**, porque `pantalla.tsx`
 * pasa `false` explícito: con Personalidad abierta la bitácora **no** se pide, y
 * volver a la pestaña **vuelve a leer** — que es lo que `CA-F6-63` necesita y lo
 * que custodia el caso `no se lee al montar la pantalla; se lee al ENTRAR`.
 */
export function SeccionDeAuditoria({
  activa = true,
}: { readonly activa?: boolean }): JSX.Element {
  const borde = useBordeConfiguracion()

  const [pagina, setPagina] = useState(1)
  const [tamanoDePagina, setTamanoDePagina] = useState<number>(TAMANO_INICIAL)
  const [filas, setFilas] = useState<readonly FilaDeAuditoria[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)

  /**
   * ⭐⭐ **SE LEE CUANDO LA PESTAÑA SE VUELVE ACTIVA, Y ESO NO ES UNA
   * OPTIMIZACIÓN: ES `CA-F6-63`.**
   *
   * `pestanas.tsx` monta LAS CINCO pestañas a la vez y apaga las inactivas con
   * `hidden`. Con una lectura atada sólo al montaje, la bitácora se pediría una
   * única vez —al abrir la pantalla, o sea **antes** de que el operador guarde
   * nada— y `CA-F6-63` (*«tras editar los textos del motor … hay al menos una
   * fila»*) **fallaría sin que nada se ponga rojo**: el operador guardaría, se
   * iría a Auditoría y vería la página vieja.
   *
   * ⇒ `activa` está en las dependencias. Volver a la pestaña vuelve a leer.
   *
   * ⚠️ **Y no es un `refetchInterval`**: no hay sondeo. `PRD_R2:2284` no pide
   * actualización automática, y una tabla que se refresca sola mientras se lee es
   * una decisión que ninguna fuente tomó.
   */
  const leer = useCallback(() => {
    if (!activa) return undefined
    let vigente = true
    borde
      .auditoria(pagina, tamanoDePagina)
      .then((leida) => {
        if (!vigente) return
        setFilas(leida.filas)
        setTotal(leida.total)
        setError(null)
      })
      .catch((causa: unknown) => {
        if (!vigente) return
        /*
         * ⛔ Las filas se dejan como estaban y NO se vacían: una tabla vacía
         * junto a un aviso de error diría «no hay cambios» y «no pude leerlos» a
         * la vez, y `CA-F6-69` le da a la tabla vacía un significado propio.
         */
        setError(causa instanceof Error ? causa.message : "No se pudo hablar con el servidor.")
      })
    return () => {
      vigente = false
    }
  }, [activa, borde, pagina, tamanoDePagina])

  useEffect(() => leer(), [leer])

  return (
    <section
      className="configuracion-chatbot__seccion"
      aria-labelledby="configuracion-chatbot-auditoria"
    >
      <h2 id="configuracion-chatbot-auditoria">Auditoría de cambios</h2>
      <p className="configuracion-chatbot__ayuda">
        Todos los cambios de configuración de la instancia, del más reciente al más antiguo.
      </p>

      {error === null ? null : (
        <p className="configuracion-chatbot__alerta" role="alert">
          {error}
        </p>
      )}

      <table className="configuracion-chatbot__tabla">
        {/*
          ⭐⭐ `[24/9/2026]` · **LA LEYENDA DE TABLA, QUE ESTA TABLA NO TENÍA.**

          `sistema-visual.md` §3.6: *«**Rótulo previo:** chip «TABLA N» … + leyenda
          de una línea en 14px `tinta-suave`»*, y el boceto de R2 la dibuja como
          `<div class="cap"><span class="chip-tabla">Tabla 1</span>Movimientos
          registrados.</div>`.

          ⛔ Va como `<caption>` y no como un `<div>` antes de la tabla, que es lo
          que el boceto hace: **es lo que hacen las otras cinco tablas de esta
          consola** (`banco`, `conversaciones`, `padron`, `metricas`,
          `carga-en-lote`), la hoja las pone `caption-side: top` y el chip lo
          estampa un `::before` con un `counter`. Un `<div>` suelto se vería igual
          y **no numeraría**: el contador sólo avanza donde hay `caption`.

          ⚠️ El texto es el del boceto. Ningún criterio nombra esta leyenda — es
          lado ② de `ADR-0319`, así que manda el boceto.
        */}
        <caption className="configuracion-chatbot__caption">Movimientos registrados.</caption>
        <thead>
          <tr>
            {/*
              ⛔ `aria-sort` y no un `disabled`: ver el encabezado del archivo.
              `PRD_R2:2284` dice que la pestaña no tiene orden, y una columna sin
              ningún control no se puede reordenar aunque no esté apagada.
            */}
            <th scope="col" aria-sort="descending">
              Fecha ↓
            </th>
            <th scope="col">Usuario</th>
            <th scope="col">Sección</th>
            <th scope="col">Cambio</th>
          </tr>
        </thead>
        <tbody>
          {filas === null ? (
            <tr>
              <td colSpan={4} className="configuracion-chatbot__vacio">
                Cargando la auditoría…
              </td>
            </tr>
          ) : filas.length === 0 ? (
            /*
              ⭐ `CA-F6-69` (`PRD_R2:2423`) · **CON EL PUNTO FINAL.** Hasta el
              21/9 decía «Todavía no hay cambios para mostrar» sin punto; `F6.md
              §6.2` lo fija con punto y el CA lo cita así.
            */
            <tr>
              <td colSpan={4} className="configuracion-chatbot__vacio">
                Todavía no hay cambios para mostrar.
              </td>
            </tr>
          ) : (
            filas.map((fila, i) => (
              /*
                ⛔⛔ **La clave es el ÍNDICE y eso está medido, no es pereza.**
                `FilaDeAuditoria` no tiene identidad propia: `AuditEntry` no lleva
                un `id` (`contracts/platform/index.ts` lo dice al hablar del
                desempate) y `A10-7` hace que un mismo cambio deje DOS filas con
                el mismo `actor`, `action`, `entity` y —hasta el milisegundo— el
                mismo `occurredAt`, una por brazo (`ADR-0258`). Una clave
                compuesta de esos cuatro campos sería DUPLICADA, que es un defecto
                de React y no una mejora. La lista es de sólo lectura, no se
                reordena y no se filtra: el índice es estable dentro de la página.
              */
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i}>
                <td>{formatearFecha(fila.occurredAt)}</td>
                {/*
                  ⛔⛔ RASTRO `[24/9/2026]` · **EL CORREO DEBAJO DEL NOMBRE SE
                  PROBÓ Y SE REVIRTIÓ: LO PROHÍBE UN CRITERIO.**

                  El boceto de R2 dibuja esta celda con dos renglones —`Andrea
                  Villalobos<span class="sub-dato">andrea.villalobos@…</span>`— y
                  acá se muestra **uno solo** (`actorName ?? actor`). Se
                  implementó… y puso rojo `gates/pantalla-auditoria.spec.ts`:

                    `[usuario-con-nombre] R2-F6-CA-65 · Usuario muestra «Admin
                     Unoadmin.uno@kai-prueba.cl» y tiene que mostrar «Admin Uno»`

                  `R2-F6-CA-65` (`PRD_R2.md:2419`) fija el contenido de ESTA celda
                  —*«la fila más reciente … tiene **Usuario «Admin Uno»**»*— y el
                  gate lo hace cumplir por **igualdad exacta**. Por `ADR-0319` ①
                  manda el criterio: se enmienda el boceto y el producto no se
                  toca. El propio gate lo dice: *«No se apaga aflojando una
                  aserción: se apaga escribiendo la pestaña»*.

                  ⚠️ Queda con dueño: si se quiere el correo —y `R2-F6-CA-71`
                  confirma que `actor` ES el correo de una cuenta real— hay que
                  cambiar `CA-65` primero, no esta línea.
                */}
                <td>{usuarioDeLaFila(fila)}</td>
                <td>{seccionDeLaEntidad(fila.entity)}</td>
                <td>{rotuloDeLaAccion(fila.action, datosDeLaFila(fila))}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/*
        ⛔ `paginado.tsx` **sin modificarlo** (`r2-ui7.md` guard 2). El selector de
        «Filas por página» va por `children`, que es el slot que `AW31` declaró
        para exactamente esto (`paginado.tsx:107-110`) y que hoy usa una sola
        pantalla. Una barra propia «porque la auditoría es distinta» es cómo
        `envios` se quedó afuera de las cuatro reglas de la hoja.
      */}
      <Paginado
        etiqueta="la auditoría"
        pagina={pagina}
        total={total}
        tamanoDePagina={tamanoDePagina}
        unidad={{ singular: "cambio", plural: "cambios" }}
        onPagina={setPagina}
      >
        <label>
          Filas por página
          {/*
            ⛔⛔ **RASTRO `[22/9/2026]` · el `aria-label` lo puso
            `gates/pantalla-auditoria.spec.ts`, y NO es redundante.** Acá había un
            `<select>` pelado adentro de este `<label>` envolvente — copiado tal
            cual de `envios/formulario.tsx:373-387`, el precedente— y el juez
            `[filas-por-pagina]` lo midió así:

                no hay ningún control cuyo rótulo sea exactamente
                «Filas por página»

            **Y tiene razón, aunque el texto esté a la vista.** Con un `<label>`
            ENVOLVENTE el nombre accesible se computa sobre el `textContent` del
            label, que incluye **el valor del control que envuelve** ⇒ el nombre
            real es «Filas por página 50», no «Filas por página», y cambia con lo
            que el operador elija. Un `<label for>`/`id` hermano también lo
            arreglaría, pero parte este nodo en DOS hijos del slot `controles` y
            **no puedo medir el efecto visual**: la regla que los acomodaría vive
            en `src/estilos.css`, que no es territorio de esta etapa.

            ⇒ `aria-label` con **el mismo texto que se ve**: nombre accesible
            determinado, cero riesgo de layout, y el `<label>` envolvente se
            queda donde estaba.

            ⚠️ `envios/formulario.tsx` tiene el MISMO defecto de nombre accesible
            y este gate no lo mira. Queda anotado en `R2-UI7.md`: no es territorio
            de esta etapa.
          */}
          <select
            aria-label="Filas por página"
            value={tamanoDePagina}
            onChange={(evento) => {
              // ⛔ Vuelve a la 1: la página 7 de una escalera de 25 no es la 7 de
              //    una de 200, y quedarse ahí muestra otras filas sin avisar.
              setPagina(1)
              setTamanoDePagina(Number(evento.target.value))
            }}
          >
            {TAMANOS_DE_PAGINA.map((tamano) => (
              <option key={tamano} value={tamano}>
                {tamano}
              </option>
            ))}
          </select>
        </label>
      </Paginado>
    </section>
  )
}
